import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DownloadCloud, PlugZap } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Panel } from "@/components/extranet/StatusBadge";
import { http, errMsg, API } from "@/lib/api";

const sel = "h-9 w-full rounded-md border border-input bg-white px-2 text-sm";

export default function ChannexPanel({ propertyId, rooms, initial, onSaved, onDone }) {
  const [f, setF] = useState({ ...initial, api_key: "", signing_secret: "" });
  const [opts, setOpts] = useState(null);
  const [busy, setBusy] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const map = (key, rid, v) => setF({ ...f, [key]: { ...(f[key] || {}), [rid]: v } });
  const hook = `${API}/channex/webhook/${initial.webhook_token}`;
  useEffect(() => { setF((x) => ({ ...x, enabled: !!initial.enabled })); }, [initial.enabled]);

  const persist = async () => {
    const { data } = await http.put(`/extranet/properties/${propertyId}/channel/channex`, f);
    setF({ ...data, api_key: "", signing_secret: "" });
  };
  const save = async () => {
    setBusy("save");
    try { await persist(); toast.success("Pengaturan Channex disimpan"); onSaved?.(); } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };
  const test = async () => {
    setBusy("test");
    try {
      await persist();
      const { data } = await http.post(`/extranet/properties/${propertyId}/channel/channex/test`);
      if (data.ok) { setOpts(data); toast.success(`Terhubung: ${data.title}`); } else toast.error(`Gagal: ${data.error}`);
      onSaved?.();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };
  const sync = async () => {
    setBusy("sync");
    try {
      const { data } = await http.post(`/extranet/properties/${propertyId}/channel/channex/sync`);
      if (data.ok) toast.success(`${data.dates} data kamar-tanggal diperbarui dari Channex`); else toast.error(`Gagal: ${data.error}`);
      onDone?.();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };
  const Pick = ({ k, rid, list, testid }) => (list
    ? <select data-testid={testid} value={f[k]?.[rid] || ""} onChange={(e) => map(k, rid, e.target.value)} className={sel}>
        <option value="">—</option>{list.map((o) => <option key={o.id} value={o.id}>{o.title}</option>)}</select>
    : <Input data-testid={testid} value={f[k]?.[rid] || ""} onChange={(e) => map(k, rid, e.target.value)} placeholder="UUID" className="h-9 font-mono-x text-xs" />);

  return (
    <Panel title="Channex" testid="channex-panel"
      action={<label className="flex items-center gap-2 text-sm font-medium">Aktif <Switch data-testid="cx-enabled-switch" checked={!!f.enabled} onCheckedChange={(v) => setF({ ...f, enabled: v })} /></label>}>
      <div className="grid gap-4 p-5 sm:grid-cols-2">
        <p className="text-xs text-slate-500 sm:col-span-2">Channex menjadi sumber utama tarif, allotment &amp; stop sell (ditarik ke BE). Booking, perubahan &amp; pembatalan dari BE otomatis dikirim ke Channex. Mengaktifkan Channex mematikan Bookandlink (pilih salah satu CM).</p>
        <div><Label>Channex Property ID</Label><Input data-testid="cx-property-id" value={f.property_id || ""} onChange={set("property_id")} placeholder="UUID properti di Channex" className="mt-1.5 font-mono-x text-xs" /></div>
        <div><Label>API key (user-api-key)</Label><Input data-testid="cx-api-key" type="password" value={f.api_key} onChange={set("api_key")} placeholder={initial.has_api_key ? "•••••• tersimpan (isi untuk mengganti)" : "Belum diisi"} className="mt-1.5" /></div>
        <div className="sm:col-span-2">
          <div className="flex items-center justify-between"><Label>Pemetaan kamar → Channex (room type &amp; rate plan)</Label>
            {!opts && <span className="text-xs text-slate-500">Klik "Tes koneksi" untuk memilih dari daftar</span>}</div>
          <div className="mt-1.5 space-y-2">
            {rooms.map((r) => (
              <div key={r.id} className="grid items-center gap-2 text-sm sm:grid-cols-[150px_1fr_1fr]" data-testid={`cx-map-row-${r.id}`}>
                <span className="truncate font-medium">{r.name}</span>
                {Pick({ k: "room_mapping", rid: r.id, list: opts?.room_types, testid: `cx-room-${r.id}` })}
                {Pick({ k: "rate_mapping", rid: r.id, list: opts?.rate_plans?.filter((x) => !f.room_mapping?.[r.id] || !x.room_type_id || x.room_type_id === f.room_mapping[r.id]), testid: `cx-rate-${r.id}` })}
              </div>
            ))}
          </div>
          <p className="mt-1 text-xs text-slate-500">Rate plan Channex yang dipilih = harga dasar kamar di BE; rate plan BE lain tetap menghitung dari harga ini.</p>
        </div>
        <div className="sm:col-span-2"><Label>Webhook ARI (tempel di Channex → Webhooks, event "ari")</Label>
          <code data-testid="cx-webhook-url" className="mt-1.5 block break-all rounded-md bg-slate-100 px-3 py-2 text-xs">{hook}</code></div>
        <div className="sm:col-span-2"><Label>Webhook signing secret (opsional, HMAC)</Label><Input data-testid="cx-signing-secret" type="password" value={f.signing_secret} onChange={set("signing_secret")} placeholder={initial.has_signing_secret ? "•••••• tersimpan" : "Kosong = tanpa verifikasi tanda tangan"} className="mt-1.5" /></div>
        {(initial.last_sync_at || initial.last_error) && (
          <p className="text-xs sm:col-span-2" data-testid="cx-status">
            {initial.last_sync_at && <span className="text-slate-500">Terakhir ditarik: {new Date(initial.last_sync_at).toLocaleString()} </span>}
            {initial.last_error && <span className="text-red-600">· {initial.last_error}</span>}</p>)}
        <div className="flex flex-wrap gap-2 sm:col-span-2">
          <button data-testid="cx-save-button" disabled={!!busy} onClick={save} className="rounded-md bg-[#006CE4] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0052B4] disabled:opacity-50">Simpan</button>
          <button data-testid="cx-test-button" disabled={!!busy} onClick={test} className="flex items-center gap-1.5 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"><PlugZap className="h-4 w-4" /> Tes koneksi</button>
          <button data-testid="cx-sync-button" disabled={!!busy || !initial.enabled} onClick={sync} className="flex items-center gap-1.5 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold hover:bg-slate-50 disabled:opacity-40"><DownloadCloud className="h-4 w-4" /> Tarik 2 tahun sekarang</button>
        </div>
      </div>
    </Panel>
  );
}
